import { useCallback, useRef } from 'react';

/**
 * One outcome per contact-step view, so a retry after a failed save or a failed advance can't log
 * the same view twice or as both Skipped and Completed:
 * - `completed(track)` when the contact info is saved (or kept as-is). It's on the account even if
 *   the flow then fails to advance, so it's recorded right away, once.
 * - `skipped(track)` only as the customer actually leaves the step without saving, and never after
 *   Completed. Call it once the flow has moved on, not when they tap skip: a skip whose advance
 *   fails leaves them here, free to type an email and complete instead.
 * Failed saves are separate SaveFailed events and don't go through here.
 */
export function useContactStepOutcome() {
  const recorded = useRef<'completed' | 'skipped' | null>(null);

  const completed = useCallback((track: () => void) => {
    if (recorded.current === 'completed') return;
    recorded.current = 'completed';
    track();
  }, []);

  const skipped = useCallback((track: () => void) => {
    if (recorded.current !== null) return;
    recorded.current = 'skipped';
    track();
  }, []);

  return { completed, skipped };
}
